import { Opportunity } from "@entities";
import {
  Button,
  Box,
  Chip,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Fade,
  FormControlLabel,
  Switch,
  ThemeProvider,
  Tooltip,
  useTheme,
} from "@mui/material";
import useCreateHyperTable from "@shared/frontend/hyper-table";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import {
  type OpportunitiesTableRowScope,
} from "@shared/helpers/opportunity-group";
import { switchToValue } from "@shared/helpers/switch-to-value";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { remult, type EntityFilter } from "remult";
import { OpportunityPreviewDialog } from "../../../components/opportunity-preview/OpportunityPreviewDialog";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { TableToolbarTabs } from "../table-tabs/TableToolbarTabs";
import { useSelectOpportunitiesColumns } from "./columns";
import defaultFilters from "./filters";
import { type RelationalOrJsonEntityFilter } from "@shared/helpers/sql-relations";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import { Roles } from "@shared/Roles";
import { useUser } from "../../../contexts/UserProvider";
import { type HyperTableFilter } from "@shared/frontend/hyper-table";

interface SelectOpportunitiesTableProps {
  open: boolean;
  onClose?: () => void;
  onSave?: (selected: string[]) => void;
  initialSelectedOpportunities?: string[];
  closeOnSave?: boolean;
  initialSelectedList?: "futureOpen" | "history" | "unHandled";
  /** When set, only open missions (like main opportunities table futureOpen tab). */
  openOnly?: boolean;
  /** Full filter set (e.g. from opportunities-table). */
  toolbarFilters?: HyperTableFilter<Opportunity>[];
  /** Extra where clause (e.g. volunteerMode for lead attendance). */
  extraWhere?: RelationalOrJsonEntityFilter<Opportunity>;
  /** Show row-scope switch like main opportunities table. */
  showRowScopeSwitch?: boolean;
  /** Open as full-screen dialog. */
  fullScreen?: boolean;
  /** Secondary action label (e.g. return to lead wizard without saving). */
  dismissLabel?: string;
  dismissTooltip?: string;
  /** Opportunity IDs that match lead/volunteer preferences (shown as badge). */
  matchingOpportunityIds?: string[];
  /** Open mission preview popup on row click instead of navigating away. */
  previewOnRowClick?: boolean;
  /** Prevent table title link from navigating to /events. */
  disableTitleNavigation?: boolean;
  /** Initial row scope for group instance visibility (lead flow uses "all"). */
  initialRowScope?: OpportunitiesTableRowScope;
}

/** Filter submenus portal to body; keep them above this dialog (and any parent dialog). */
const FILTER_SUBMENU_Z_INDEX_OFFSET = 8;

const SelectOpportunitiesTable: React.FC<SelectOpportunitiesTableProps> = (
  props,
) => {
  const {
    open,
    onClose,
    initialSelectedOpportunities = [],
    initialSelectedList = "futureOpen",
    onSave,
    closeOnSave,
    openOnly = false,
    toolbarFilters,
    extraWhere,
    showRowScopeSwitch = false,
    fullScreen = false,
    dismissLabel,
    dismissTooltip,
    matchingOpportunityIds = [],
    previewOnRowClick = false,
    disableTitleNavigation = false,
    initialRowScope = "roots",
  } = props;
  const { currOrganization } = useOrganization();
  const theme = useTheme();
  const dialogOverlayTheme = useMemo(() => {
    const menuZIndex = theme.zIndex.modal + FILTER_SUBMENU_Z_INDEX_OFFSET;
    return createTheme(theme, {
      zIndex: { modal: menuZIndex },
      components: {
        MuiModal: {
          styleOverrides: { root: { zIndex: menuZIndex } },
        },
        MuiPopover: {
          styleOverrides: { root: { zIndex: menuZIndex } },
        },
        MuiPopper: {
          styleOverrides: { root: { zIndex: menuZIndex } },
        },
        MuiMenu: {
          styleOverrides: { root: { zIndex: menuZIndex } },
        },
      },
    });
  }, [theme]);
  const [selectedOpportunities, setSelectedOpportunities] = useState<string[]>(
    initialSelectedOpportunities,
  );
  const [selectedList, setSelectedList] = useState<
    "futureOpen" | "history" | "unHandled"
  >(openOnly ? "futureOpen" : initialSelectedList);
  const [rowScope, setRowScope] = useState<OpportunitiesTableRowScope>(initialRowScope);
  const [previewOpportunityId, setPreviewOpportunityId] = useState<string | null>(null);
  const [previewView, setPreviewView] = useState<"details" | "group-slots">(
    "details",
  );
  const { currentUser } = useUser();
  const { HyperTable } = useCreateHyperTable(Opportunity);
  const columns = useSelectOpportunitiesColumns();
  const filters = toolbarFilters ?? defaultFilters;
  const matchingIdSet = useMemo(
    () => new Set(matchingOpportunityIds),
    [matchingOpportunityIds],
  );

  const handleClose = useCallback(() => {
    setPreviewOpportunityId(null);
    setPreviewView("details");
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setRowScope(initialRowScope);
    }
  }, [open, initialRowScope]);

  const where = useMemo<RelationalOrJsonEntityFilter<Opportunity>>(() => {
    const now = new Date();
    const usesSqlTabSignals =
      openOnly || selectedList === "futureOpen" || selectedList === "unHandled";
    const listFilter = usesSqlTabSignals
      ? ({} as EntityFilter<Opportunity>)
      : switchToValue<typeof selectedList, EntityFilter<Opportunity>>(
          selectedList,
        )
          .case("history", {
            endDate: { $lt: now },
          })
          .done();

    return {
      $and: [
        {
          organization: { $id: currOrganization?.id ?? "" },
          status: { $ne: "AID_REQUEST" },
          teams: {
            $some: remult.isAllowed(Roles.Admins)
              ? {}
              : {
                teamId: { $in: currentUser?.teams?.map((team) => team.teamId) || [] },
              }
          }
        },
        { ...listFilter } as RelationalOrJsonEntityFilter<Opportunity>,
        ...(extraWhere ? [extraWhere as RelationalOrJsonEntityFilter<Opportunity>] : []),
      ],
    };
  }, [currOrganization?.id, selectedList, currentUser?.teams, openOnly, extraWhere]);

  useEffect(() => {
    if (open) {
      setSelectedList(openOnly ? "futureOpen" : initialSelectedList);
      setSelectedOpportunities(initialSelectedOpportunities);
    }
  }, [open, openOnly, initialSelectedList, initialSelectedOpportunities]);

  const getRowSelection = useCallback((selected: string[]) => {
    return selected.reduce((obj, prev) => ({ ...obj, [prev]: true }), {});
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      transitionDuration={400}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          ...(fullScreen
            ? {}
            : {
                maxWidth: "100vw",
                maxHeight: "100vh",
                width: "96vw",
                height: "90vh",
              }),
        },
      }}
      disablePortal={false}
    >
      <ThemeProvider theme={dialogOverlayTheme}>
      <DialogContent
        sx={{
          position: "relative",
          padding: 1.5,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <HyperTable
          tableId="select-opportunities-table"
          getRowClassName={(row) =>
            !currentUser?.isCallCenterStation &&
            row.original.callCenterInVolunteerPool
              ? "table-row--call-center-pool"
              : undefined
          }
          onRowClick={
            previewOnRowClick
              ? (_, row) => {
                  const opportunity = row.original;
                  const isGroupParent =
                    !!opportunity.isOpportunityGroupParent ||
                    (allowsGroupVolunteer(opportunity.volunteerMode) &&
                      !opportunity.parentOpportunityId);
                  setPreviewView(isGroupParent ? "group-slots" : "details");
                  setPreviewOpportunityId(opportunity.id);
                }
              : undefined
          }
          findConfig={{
            where: {
              $and: [
                ...(openOnly ||
                selectedList === "futureOpen" ||
                selectedList === "unHandled"
                  ? [
                      Opportunity.opportunitiesTableOpenTabsEligibility(),
                      Opportunity.matchesOpportunitiesTableTabSignals({
                        tab:
                          selectedList === "unHandled" ? "unHandled" : "futureOpen",
                        rowScope,
                      }),
                    ]
                  : []),
                Opportunity.hyperFilter(where),
              ],
            },
            enabled: !!currOrganization,
            pinnedOrderBy: {
              handlingOwnFirst: "asc",
              ...(!currentUser?.isCallCenterStation
                ? { callCenterInVolunteerPool: "asc" as const }
                : {}),
              callCenterUrgency: "desc",
            },
            initialOrderBy: {
              handlingOwnFirst: "asc",
              ...(selectedList === "history" ? { endDate: "desc" as const } : {}),
            },
          }}
          title={
            <TableHeaderTitles
              titles={[{ content: "משימות", path: disableTitleNavigation ? undefined : "/events" }]}
              disableNavigation={disableTitleNavigation}
            />
          }
          // OnDisplayedDataChange={() => {
          //   const table = HyperTable.context.current?.table;
          //   if (table) {
          //     table
          //       .getRowModel()
          //       .flatRows.forEach((r) =>
          //         r.toggleSelected(
          //           selectedOpportunities.includes(r.original.id),
          //         ),
          //       );
          //   }
          // }}
          tableSettings={{
            state: {
              rowSelection: getRowSelection(selectedOpportunities),
            },
            onRowSelectionChange: (newState) => {
              setSelectedOpportunities((prev) => {
                const p = getRowSelection(prev);
                const newValue =
                  newState instanceof Function ? newState(p) : newState;
                return Object.keys(newValue);
              });
            },
          }}
        >
          <HyperTable.Toolbar>
            {filters.map((filter) =>
              isEntityFilter(filter) ? (
                <HyperTable.EntityFilter {...(filter as any)} key={filter.id} />
              ) : (
                <HyperTable.CustomFilter {...filter} key={filter.id} />
              ),
            )}
            {showRowScopeSwitch && (
              <FormControlLabel
                sx={{ mr: 1 }}
                control={
                  <Switch
                    size="small"
                    checked={rowScope === "all"}
                    onChange={(_, checked) =>
                      setRowScope(checked ? "all" : "roots")
                    }
                  />
                }
                label="הצג מופעים בנפרד"
              />
            )}
          </HyperTable.Toolbar>
          {!openOnly && (
            <TableToolbarTabs
              value={selectedList}
              onChange={setSelectedList}
              options={[
                { content: "פתוחות", value: "futureOpen" },
                { content: "לא טופלו", value: "unHandled" },
                { content: "היסטוריה", value: "history" },
              ]}
            />
          )}
          <HyperTable.SelectColumn
            selectAllBehavior="single-page"
          // OnItemChange={(item, isSelected) => {
          //   setSelectedOpportunities((prev) => {
          //     const copy = [...prev];
          //     const index = copy.indexOf(item.id);
          //     if (isSelected) {
          //       if (index !== -1) return copy;
          //       copy.push(item.id);
          //     } else {
          //       if (index === -1) return copy;
          //       copy.splice(index, 1);
          //     }
          //     return copy;
          //   });
          // }}
          />
          {matchingIdSet.size > 0 && (
            <HyperTable.Column
              field="id"
              children="התאמה לליד"
              enableSorting={false}
              cell={({ row }) =>
                matchingIdSet.has(row.original.id) ? (
                  <Chip size="small" label="מתאים" color="primary" variant="outlined" />
                ) : null
              }
            />
          )}
          {columns.map((column) => (
            <HyperTable.Column key={column.field} {...column} />
          ))}
        </HyperTable>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0 1rem 1rem 1rem", gap: 1 }}>
        <DialogContent sx={{ overflow: "hidden", flex: 1, p: 0 }}>
          <DialogContentText fontWeight={500} color="secondary">
            נבחרו {selectedOpportunities.length} משימות
          </DialogContentText>
        </DialogContent>
        {dismissLabel ? (
          <Tooltip title={dismissTooltip ?? ""} arrow disableHoverListener={!dismissTooltip}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              {dismissLabel}
            </Button>
          </Tooltip>
        ) : null}
        <Button
          variant="contained"
          onClick={() => {
            onSave?.(selectedOpportunities);
            if (closeOnSave) {
              onClose?.();
            }
          }}
          color="secondary"
        >
          בחירה
        </Button>
      </DialogActions>
      {previewOnRowClick ? (
        <OpportunityPreviewDialog
          open={!!previewOpportunityId}
          opportunityId={previewOpportunityId}
          view={previewView}
          onClose={() => {
            setPreviewOpportunityId(null);
            setPreviewView("details");
          }}
          nestedInDialog
        />
      ) : null}
      </ThemeProvider>
    </Dialog>
  );
};

export default SelectOpportunitiesTable;
